import React, { useEffect, useRef, useState } from "react";
import "../styles/About.css";

const CARDS = [
  {
    num: "01",
    title: "Experience",
    body: "3+ years designing and developing full-stack applications with modern UI/UX principles — from concept to deployed product.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
        <line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
      </svg>
    ),
  },
  {
    num: "02",
    title: "Entrepreneurship",
    body: "Founder of Codix Tech & GoldTech Media — delivering web solutions and media projects that help businesses establish powerful digital presences.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
      </svg>
    ),
  },
  {
    num: "03",
    title: "Full-Stack Expertise",
    body: "ReactJS, Node.js, PHP, databases, UI/UX design, AI integration, SEO — a complete skill set from pixel to production.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
      </svg>
    ),
  },
  {
    num: "04",
    title: "Published Author",
    body: "Author of WEB101: How Your Website Drives Business Growth — a business-focused guide bridging design, development, and real revenue results.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
      </svg>
    ),
  },
];

const TIMELINE = [
  { year: "2022", event: "Launched GoldTech Media" },
   { year: "2025", event: "Founded Codix Tech" },
  { year: "2025", event: "Published WEB101" },
  { year: "2025", event: "Coding Instructor" },
];

const AboutCard = ({ card, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`about-card ${visible ? "about-card--visible" : ""}`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <div className="about-card-header">
        <span className="about-card-num">{card.num}</span>
        <span className="about-card-icon">{card.icon}</span>
      </div>
      <h3 className="about-card-title">{card.title}</h3>
      <p className="about-card-body">{card.body}</p>
      <div className="about-card-line" />
    </div>
  );
};

const About = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="about" id="about" ref={sectionRef}>
      {/* horizontal rule decoration */}
      <div className="about-rule" aria-hidden="true" />

      <div className="about-container">
        {/* ── Header ── */}
        <div className={`about-header ${visible ? "about-header--visible" : ""}`}>
          <span className="section-label">// about me</span>
          <h2 className="section-title">
            The mind <em>behind</em><br />the code
          </h2>
          <div className="divider" />
          <p className="about-intro">
            I'm <strong>Roy Zaouk</strong> — a Web Developer, UI/UX Designer, BA in Computer
            Science, founder of <strong>Codix Tech</strong> & <strong>GoldTech Media</strong>,
            and Coding Instructor. I build modern web solutions and scalable digital platforms
            that convert visitors into customers. I'm also the author of{" "}
            <em>WEB101: How Your Website Drives Business Growth</em>.
          </p>
        </div>

        {/* ── Two-column layout ── */}
        <div className="about-body">
          {/* Cards grid */}
          <div className="about-cards">
            {CARDS.map((card, i) => (
              <AboutCard key={card.num} card={card} index={i} />
            ))}
          </div>

          {/* Right column: timeline + portrait placeholder */}
          <div className="about-aside">
            <div className="about-portrait">
              <div className="about-portrait-inner">
                <span className="about-portrait-initials">RZ</span>
              </div>
              <div className="about-portrait-ring" aria-hidden="true" />
            </div>

            <div className="about-timeline">
              {TIMELINE.map(({ year, event }, i) => (
                <div
                  key={year}
                  className={`about-timeline-item ${visible ? "about-timeline-item--visible" : ""}`}
                  style={{ transitionDelay: `${300 + i * 120}ms` }}
                >
                  <span className="about-timeline-year">{year}</span>
                  <div className="about-timeline-dot" />
                  <span className="about-timeline-event">{event}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;